import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  Input,
  Text,
  VStack,
  Flex,
  Avatar,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  useToast,
} from "@chakra-ui/react";
import { cqAPI, getBid } from "../../lib/Auth";
import AsyncSelect from "react-select/async";
import { useNavigate } from "react-router-dom";

function CreateGroupModal({ isOpen, onClose }) {
  const [groupName, setGroupName] = useState("");
  const [participants, setParticipants] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const bid = getBid();

  const loadStaffOptions = async (inputValue) => {
    try {
      const response = await cqAPI.get(`/a-dec/findProfiles?query=${inputValue}`);
      if (response.type === "ok") {
        return (response.data || [])
          .filter((p) => !participants.some((s) => s.value === p.id))
          .map((p) => ({
            value: p.id,
            label: p.name || p.profile_name || p.email,
          }));
      }
      return [];
    } catch {
      return [];
    }
  };

  const handleSelectUser = (option) => {
    if (option && !participants.some((p) => p.value === option.value)) {
      setParticipants([...participants, option]);
    }
  };

  const handleRemoveParticipant = (userId) => {
    setParticipants(participants.filter((p) => p.value !== userId));
  };

  const handleCreate = async () => {
    if (!groupName.trim()) {
      toast({
        title: "Group name is required.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    if (participants.length < 1) {
      toast({
        title: "Add at least one participant.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsCreating(true);

    const response = await cqAPI.post("/group-chat", {
      name: groupName.trim(),
      participants: participants.map((p) => p.value),
    });

    setIsCreating(false);

    if (response.type === "ok") {
      toast({
        title: "Group created!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      setGroupName("");
      setParticipants([]);
      onClose();
      // Navigate to the new group
      navigate(`/team/group/${response.data._id}?bid=${bid}`);
    } else {
      toast({
        title: "Error",
        description: response.msg,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleClose = () => {
    setGroupName("");
    setParticipants([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Group Chat</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Input
              placeholder="Group name"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
            />
            <Text fontSize="sm" fontWeight="600">
              Add Participants
            </Text>
            <AsyncSelect
              cacheOptions
              defaultOptions
              loadOptions={loadStaffOptions}
              onChange={handleSelectUser}
              value={null}
              placeholder="Search staff..."
              menuPlacement="auto"
            />
            {participants.length > 0 && (
              <Wrap>
                {participants.map((p) => (
                  <WrapItem key={p.value}>
                    <Tag size="md" borderRadius="full" variant="solid" colorScheme="blue">
                      <Avatar size="xs" name={p.label} ml={-1} mr={2} />
                      <TagLabel>{p.label}</TagLabel>
                      <TagCloseButton onClick={() => handleRemoveParticipant(p.value)} />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleCreate}
            isLoading={isCreating}
            isDisabled={!groupName.trim() || participants.length < 1}
          >
            Create Group
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CreateGroupModal;
